import { Conversation, Message, Participant, User } from '@twilio/conversations'

import { formatMessage } from '../useChatHelper'
import { MessageProps } from '../../../../types/chat'
import { executeResponseError } from '../../../../helpers'
import { ChatChannelBaseData } from './useChannelHelperTypes'

const NUM_OF_MESSAGES = 100

export const getLastSeenIndexes = (
	participants: Participant[],
	userIdentity: string
): number[] => {
	const lastSeenIndexes: number[] = []

	participants?.forEach((participant: Participant) => {
		if (participant?.lastReadMessageIndex) {
			lastSeenIndexes[0] = participant.lastReadMessageIndex
		}

		if (userIdentity && participant?.identity !== userIdentity) {
			lastSeenIndexes[1] = participant.lastReadMessageIndex || -1
		}
	})

	return lastSeenIndexes
}

export const getUsersModelsForParticipants = async (
	participants: Participant[]
): Promise<User[]> => {
	try {
		const userPromises = participants.map(participant =>
			participant.getUser()
		)

		if (!userPromises || !userPromises.length) return []

		return await Promise.all(userPromises)
	} catch (e: any) {
		throw new Error('Get user models for participants error')
	}
}

export const getChatChannelData = async (
	channel: Conversation,
	userIdentity: string
): Promise<ChatChannelBaseData | undefined> => {
	try {
		const messages = await channel?.getMessages(NUM_OF_MESSAGES)

		const channelMessages = messages?.items?.reduce(
			(acc: MessageProps[], item: Message) => {
				return [...acc, formatMessage(item)]
			},
			[]
		)

		const participants = await channel?.getParticipants()

		const [lastSeenIndex, lastSeenPatientIndex] = getLastSeenIndexes(
			participants,
			userIdentity
		)

		const userModelsForParticipants = await getUsersModelsForParticipants(
			participants
		)

		const participant = participants?.find(p => p.identity !== userIdentity)
		const participantData = await participant?.getUser()

		return {
			channelMessages,
			lastSeenIndex,
			lastSeenPatientIndex,
			userModelsForParticipants,
			isPatientOnline: !!participantData?.isOnline,
		}
	} catch (e: any) {
		const error = executeResponseError(e, 'Get channel data error')

		throw new Error(error || '')
	}
}

export const getUnreadMessages = (
	lastMsgIndex: number | undefined | null,
	lastReadMessageIndex: number | undefined | null
): number | undefined => {
	if (!lastReadMessageIndex || !lastMsgIndex) return

	return lastMsgIndex - lastReadMessageIndex
}
